import React, { ReactNode, useContext, useState } from 'react';
import classNames from 'classnames';
import Preferences from '../Preferences';
import { Collapsible } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import { Field } from 'react-final-form';
import InputField from 'components/InputField';
import validations from 'validations';
import ListingCreationSelectField from 'router/subrouters/ListingCreationWizard/components/ListingCreationSelectField';
import ToggleField from 'components/ToggleField';
import {
  ACQUISITION_TIMELINE,
  GM_IN_PLACE,
  MANAGEMENT_TEAM,
  PREFERRED_REVENUE_MODELS,
} from 'models/User';
import ProfileActions from '../../components/ProfileActions';
import CurrentProfileContext from '../../providers/CurrentProfile/CurrentProfile.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';

import './PreferencesPage.styles.scss';
import './PreferencesPage.styles.responsive.scss';

type PreferencesPageProps = {
  className?: string;
};

type PreferencesPageCollapseSectionProps = {
  className?: string;
  triggerContent?: ReactNode;
  defaultOpen?: boolean;
};

const PrefPageCollapsSection: React.FC<PreferencesPageCollapseSectionProps> = ({
  children,
  className,
  triggerContent,
  defaultOpen,
}) => {
  const [isOpen, setIsOpen] = useState(!!defaultOpen);
  const classes = classNames(
    'className',
    'pref-page-collaps-section',
    { open: isOpen },
    className,
  );

  return (
    <Collapsible
      open={isOpen}
      onAnimationEnd={setIsOpen}
      trigger={
        <div className="pref-page-collaps-section__trigger">
          {triggerContent}
          <ChevrondownIcon />
        </div>
      }
      className={classes}
    >
      {children}
    </Collapsible>
  );
};

const PreferencesPage: React.FC<PreferencesPageProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();

  const { isMyProfile } = useContext(CurrentProfileContext);
  const {
    currentUser: { hasVerifiedEmail },
    openVerificationModal,
  } = useContext(CurrentUserContext);

  const classes = classNames(
    'preferences-page',
    'profile-card-container',
    className,
  );

  const createSelectOptions = useCreateSelectOptions();

  return (
    <div className={classes}>
      <PrefPageCollapsSection
        defaultOpen
        triggerContent={
          <span className="profile-section-title">
            {t('acquisitionPreferences')}
          </span>
        }
      >
        <Preferences />
      </PrefPageCollapsSection>
      <PrefPageCollapsSection
        triggerContent={
          <span className="profile-section-title">{t('financials')}</span>
        }
      >
        <div className="financial-cont">
          <div className="form-row">
            <Field
              name="ebitda"
              label={t('ebitda')}
              numberStyle="dottedNumber"
              component={InputField}
              placeholder="$"
              validate={validations.composeValidators(
                validations.positiveNumbersOnly(
                  t('onlyPositiveNumbersAreAllowed'),
                ),
              )}
            />
            <div className="form-row">
              <Field
                name="ebitdaMin"
                label={t('ebidtaMulMinFrom')}
                numberStyle="dottedNumber"
                component={InputField}
                placeholder="$"
                validate={validations.composeValidators(
                  validations.positiveNumbersOnly(
                    t('onlyPositiveNumbersAreAllowed'),
                  ),
                )}
              />
              <Field
                name="ebitdaMax"
                label={t('ebidtaMulMaxTo')}
                numberStyle="dottedNumber"
                component={InputField}
                placeholder="$"
                validate={validations.composeValidators(
                  validations.positiveNumbersOnly(
                    t('onlyPositiveNumbersAreAllowed'),
                  ),
                  validations.isGreatherThanField('ebidtaMulMinFrom'),
                )}
              />
            </div>
          </div>

          <div className="form-row">
            <Field
              name="revenueMin"
              label={t('revenueMin')}
              numberStyle="dottedNumber"
              component={InputField}
              placeholder="$"
              validate={validations.composeValidators(
                validations.positiveNumbersOnly(
                  t('onlyPositiveNumbersAreAllowed'),
                ),
              )}
            />

            <Field
              name="preferredRevenueModels"
              label={t('revenueModelLabel')}
              options={createSelectOptions(
                Object.values(PREFERRED_REVENUE_MODELS),
              )}
              component={ListingCreationSelectField}
              withShowMore={false}
            />
          </div>
        </div>
      </PrefPageCollapsSection>
      <PrefPageCollapsSection
        triggerContent={
          <span className="profile-section-title">
            {t('advancedPreferences')}
          </span>
        }
      >
        <div className="advanced-pref-cont">
          <Field
            name="managementTeam"
            label={t('managementTeam')}
            options={createSelectOptions(Object.values(MANAGEMENT_TEAM))}
            component={ListingCreationSelectField}
            withShowMore={false}
          />

          <Field
            name="gmInPlace"
            label={t('gmInPlaceWouldBe')}
            options={createSelectOptions(Object.values(GM_IN_PLACE))}
            component={ListingCreationSelectField}
            withShowMore={false}
          />

          <Field
            name="acquistionTimeline"
            label={t('acquisitionTimeline')}
            options={createSelectOptions(Object.values(ACQUISITION_TIMELINE))}
            component={ListingCreationSelectField}
            withShowMore={false}
          />

          <p>{t('lastly')}</p>

          <Field
            name="interestedInMoreBusinesses"
            type="checkbox"
            component={ToggleField}
            label={t('interestedInAcq')}
          />

          <Field
            name="interestedInCommercialProperty"
            type="checkbox"
            component={ToggleField}
            label={t('interestedInCommercial')}
          />
        </div>
      </PrefPageCollapsSection>

      <ProfileActions
        onSubmit={
          isMyProfile && !hasVerifiedEmail
            ? () => openVerificationModal()
            : null
        }
      />
    </div>
  );
};

export default PreferencesPage;
